import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { RotateCcw, Move3d } from "lucide-react";
import { hardwareFinishColor } from "@/lib/hardware";

/**
 * A CSS-3D "spinnable" door preview.
 * Renders a real-depth 3D door slab (front + back + 4 side faces) using
 * `transform-style: preserve-3d`. The user can drag horizontally to rotate
 * the door around its Y axis. A subtle auto-sway plays until the user
 * interacts. On category change the door does a signature swing-in reveal.
 */
export default function Door3D({
  image,
  imageFit = "cover",
  colorHex,
  category = "Exterior",
  seriesName,
  stepIndex = 0,
  stepTotal = 5,
  stepLabel = "",
  handleFinish,
}) {
  const [rotY, setRotY] = useState(-14);
  const [rotX, setRotX] = useState(-2);
  const [dragging, setDragging] = useState(false);
  const [hintVisible, setHintVisible] = useState(true);
  const containerRef = useRef(null);
  const dragRef = useRef({ startX: 0, startY: 0, startRotY: 0, startRotX: 0 });
  const idleRaf = useRef(null);
  const hasInteracted = useRef(false);

  // Auto-idle sway
  useEffect(() => {
    let start = performance.now();
    const loop = (t) => {
      if (!hasInteracted.current) {
        const delta = (t - start) / 1000;
        setRotY(-14 + Math.sin(delta * 0.6) * 8);
        setRotX(-2 + Math.cos(delta * 0.4) * 2);
      }
      idleRaf.current = requestAnimationFrame(loop);
    };
    idleRaf.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(idleRaf.current);
  }, []);

  // Swing-in on category change
  useEffect(() => {
    hasInteracted.current = false;
    setRotY(-70);
    setRotX(-2);
    const t = setTimeout(() => setRotY(-14), 60);
    return () => clearTimeout(t);
  }, [category]);

  // Hide hint after a few seconds
  useEffect(() => {
    const t = setTimeout(() => setHintVisible(false), 3800);
    return () => clearTimeout(t);
  }, []);

  const onPointerDown = (e) => {
    e.preventDefault();
    hasInteracted.current = true;
    setHintVisible(false);
    setDragging(true);
    const isTouch = e.touches && e.touches[0];
    const p = isTouch ? e.touches[0] : e;
    dragRef.current = {
      startX: p.clientX,
      startY: p.clientY,
      startRotY: rotY,
      startRotX: rotX,
    };
    window.addEventListener("mousemove", onPointerMove);
    window.addEventListener("mouseup", onPointerUp);
    window.addEventListener("touchmove", onPointerMove, { passive: false });
    window.addEventListener("touchend", onPointerUp);
  };

  const onPointerMove = (e) => {
    if (e.cancelable && e.touches) e.preventDefault();
    const p = e.touches && e.touches[0] ? e.touches[0] : e;
    const dx = p.clientX - dragRef.current.startX;
    const dy = p.clientY - dragRef.current.startY;
    const next = dragRef.current.startRotY + dx * 0.55;
    const nextX = Math.max(-16, Math.min(6, dragRef.current.startRotX - dy * 0.15));
    setRotY(Math.max(-95, Math.min(95, next)));
    setRotX(nextX);
  };

  const onPointerUp = () => {
    setDragging(false);
    window.removeEventListener("mousemove", onPointerMove);
    window.removeEventListener("mouseup", onPointerUp);
    window.removeEventListener("touchmove", onPointerMove);
    window.removeEventListener("touchend", onPointerUp);
  };

  const reset = () => {
    hasInteracted.current = false;
    setRotY(-14);
    setRotX(-2);
  };

  const edgeColor = colorHex || "#2A2622";
  const preservePhoto = imageFit === "contain";

  return (
    <div
      ref={containerRef}
      className="relative bg-[#141210] overflow-hidden select-none aspect-[4/5] md:aspect-[4/4.4]"
      data-testid="door3d-preview"
      style={{
        perspective: "1600px",
        perspectiveOrigin: "50% 40%",
        cursor: dragging ? "grabbing" : "grab",
        touchAction: "none",
      }}
      onMouseDown={onPointerDown}
      onTouchStart={onPointerDown}
    >
      {/* Ambient background — subtle vignette + floor shadow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 50% 40%, rgba(255,240,220,0.08) 0%, rgba(0,0,0,0) 55%), radial-gradient(ellipse at 50% 100%, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 60%)",
        }}
      />

      {/* Floor plane */}
      <div
        aria-hidden
        className="absolute left-0 right-0 bottom-0 pointer-events-none"
        style={{
          height: "42%",
          background:
            "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.35) 30%, rgba(0,0,0,0.55) 100%)",
        }}
      />

      {/* 3D stage */}
      <div
        className="absolute inset-0 flex items-center justify-center"
        style={{ transformStyle: "preserve-3d" }}
      >
        <motion.div
          animate={{ rotateY: rotY, rotateX: rotX }}
          transition={
            dragging
              ? { duration: 0 }
              : { type: "spring", damping: 22, stiffness: 90, mass: 1.1 }
          }
          className="relative"
          style={{
            width: preservePhoto ? "88%" : "56%",
            height: preservePhoto ? "auto" : "82%",
            aspectRatio: preservePhoto ? "4 / 3" : undefined,
            transformStyle: "preserve-3d",
          }}
        >
          {/* FRONT face */}
          <div
            className="absolute inset-0 overflow-hidden shadow-[0_40px_120px_rgba(0,0,0,0.65)]"
            style={{
              backfaceVisibility: "hidden",
              transform: "translateZ(14px)",
              background: "#0a0a0a",
            }}
          >
            <AnimatePresence mode="wait">
              <motion.img
                key={image}
                src={image}
                alt={seriesName ? `${seriesName} door` : "Configured door"}
                className={`w-full h-full ${preservePhoto ? "object-contain" : "object-cover"}`}
                data-testid="door3d-product-image"
                initial={{ opacity: 0, scale: preservePhoto ? 1 : 1.08 }}
                animate={{ opacity: 1, scale: preservePhoto ? 1 : 1.02 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                draggable={false}
              />
            </AnimatePresence>
            {colorHex && (
              <div
                className="absolute inset-0 mix-blend-multiply pointer-events-none"
                style={{ background: colorHex, opacity: 0.28 }}
              />
            )}
            {/* Light gradient */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "linear-gradient(115deg, rgba(255,240,220,0.14) 0%, rgba(0,0,0,0) 35%, rgba(0,0,0,0.35) 100%)",
              }}
            />
            {/* Handle indicator on right edge */}
            {!preservePhoto && <div
              data-testid="door-preview-handle-indicator"
              className="absolute right-[6%] top-1/2 -translate-y-1/2 w-[3px] h-[70px] rounded"
              style={{
                background: hardwareFinishColor(handleFinish),
                boxShadow: "0 0 8px rgba(0,0,0,0.4)",
              }}
              aria-hidden
            />}
          </div>

          {/* BACK face */}
          <div
            className="absolute inset-0 overflow-hidden"
            style={{
              backfaceVisibility: "hidden",
              transform: "translateZ(-14px) rotateY(180deg)",
              background: edgeColor,
            }}
          >
            <div className="absolute inset-0 opacity-40" style={{ background: `linear-gradient(180deg, rgba(255,255,255,0.06), rgba(0,0,0,0.35))` }} />
          </div>

          {/* RIGHT edge */}
          <div
            className="absolute top-0 right-0 h-full"
            style={{
              width: "28px",
              transform: "rotateY(90deg) translateZ(calc(100% - 14px))",
              transformOrigin: "right center",
              background: edgeColor,
              boxShadow: "inset 0 0 40px rgba(0,0,0,0.55)",
            }}
          />
          {/* LEFT edge */}
          <div
            className="absolute top-0 left-0 h-full"
            style={{
              width: "28px",
              transform: "rotateY(-90deg) translateZ(14px)",
              transformOrigin: "left center",
              background: edgeColor,
              boxShadow: "inset 0 0 40px rgba(0,0,0,0.55)",
            }}
          />
          {/* TOP edge */}
          <div
            className="absolute left-0 top-0 w-full"
            style={{
              height: "28px",
              transform: "rotateX(90deg) translateZ(14px)",
              transformOrigin: "top center",
              background: edgeColor,
              boxShadow: "inset 0 0 40px rgba(0,0,0,0.6)",
            }}
          />
          {/* BOTTOM edge */}
          <div
            className="absolute left-0 bottom-0 w-full"
            style={{
              height: "28px",
              transform: "rotateX(-90deg) translateZ(14px)",
              transformOrigin: "bottom center",
              background: edgeColor,
              boxShadow: "inset 0 0 40px rgba(0,0,0,0.7)",
            }}
          />
        </motion.div>
      </div>

      {/* Category / series chips */}
      <div className="absolute top-5 left-5 flex flex-col gap-2 pointer-events-none">
        <span className="bg-brand-green/85 text-brand-bone text-[0.62rem] tracking-[0.32em] uppercase px-3 py-1.5 border border-brand-gold/25 backdrop-blur">
          {category}
        </span>
        {seriesName && (
          <span className="bg-brand-charcoal/70 text-brand-bone text-[0.62rem] tracking-[0.32em] uppercase px-3 py-1.5 border border-brand-gold/25 backdrop-blur">
            {seriesName}
          </span>
        )}
      </div>

      {/* Reset button */}
      <button
        type="button"
        onClick={reset}
        className="absolute top-5 right-5 flex items-center gap-2 text-[0.62rem] tracking-[0.28em] uppercase text-brand-bone/80 hover:text-brand-gold border border-brand-bone/20 hover:border-brand-gold px-3 py-1.5 backdrop-blur bg-brand-charcoal/50"
        data-testid="door3d-reset"
        aria-label="Reset rotation"
      >
        <RotateCcw size={12} /> Reset
      </button>

      {/* Step counter */}
      <div className="absolute bottom-5 right-5 text-brand-bone/90 pointer-events-none">
        <div className="font-serif italic text-4xl md:text-5xl text-brand-gold leading-none tabular-nums">
          {String(stepIndex + 1).padStart(2, "0")}
          <span className="text-brand-bone/60 text-2xl">/{String(stepTotal).padStart(2, "0")}</span>
        </div>
        <div className="mt-1 text-[0.62rem] tracking-[0.32em] uppercase text-brand-bone/70">
          {stepLabel}
        </div>
      </div>

      {/* Drag hint */}
      <AnimatePresence>
        {hintVisible && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="absolute bottom-5 left-5 pointer-events-none flex items-center gap-2 text-[0.62rem] tracking-[0.32em] uppercase text-brand-bone/85 bg-brand-charcoal/60 backdrop-blur border border-brand-gold/25 px-3 py-1.5"
            data-testid="door3d-hint"
          >
            <Move3d size={12} className="text-brand-gold" /> Drag to rotate
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
